import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { storage } from "./storage";
import api, { setAuthToken } from "./api";

const isNative = Capacitor.isNativePlatform();
let listenersRegistered = false;
let pendingDeviceToken = null; // Store token if registration is pending

// Helper to ensure auth token is set before API calls
const ensureAuthToken = async () => {
  const token = await storage.getItem("skybday_token");
  console.log("[PUSH] ensureAuthToken - token exists:", !!token);
  if (token) {
    setAuthToken(token);
    return true;
  }
  return false;
};

// Register device token with backend
export const registerDeviceWithBackend = async (deviceToken) => {
  console.log("[PUSH] ========================================");
  console.log("[PUSH] registerDeviceWithBackend called");
  console.log("[PUSH] Token to register:", deviceToken ? deviceToken.substring(0, 40) + "..." : "null");
  
  if (!deviceToken) {
    console.log("[PUSH] ERROR: No device token provided!");
    return false;
  }
  
  // Ensure auth token is set
  const hasAuth = await ensureAuthToken();
  console.log("[PUSH] Auth token check result:", hasAuth);
  
  if (!hasAuth) {
    console.log("[PUSH] No auth token available, saving token for later registration");
    pendingDeviceToken = deviceToken;
    return false;
  }
  
  try {
    const platform = Capacitor.getPlatform();
    console.log(`[PUSH] Making API call: POST /notifications/register-device`);
    console.log(`[PUSH] Platform: ${platform}`);
    console.log(`[PUSH] Token: ${deviceToken.substring(0, 40)}...`);
    
    const response = await api.post("/notifications/register-device", {
      token: deviceToken,
      platform: platform,
    });
    
    console.log("[PUSH] ✅ Device registered successfully!");
    console.log("[PUSH] Response:", JSON.stringify(response.data));
    pendingDeviceToken = null; // Clear pending token on success
    return true;
  } catch (error) {
    console.error("[PUSH] ❌ Failed to register device token!");
    console.error("[PUSH] Error status:", error?.response?.status);
    console.error("[PUSH] Error data:", JSON.stringify(error?.response?.data));
    console.error("[PUSH] Error message:", error.message);
    
    // Check if it's an auth error
    if (error?.response?.status === 401) {
      console.error("[PUSH] Auth error - token might be invalid or expired");
      pendingDeviceToken = deviceToken; // Save for retry
    }
    
    return false;
  }
};

// Register any pending device token (call after login)
export const registerPendingDeviceToken = async () => {
  console.log("[PUSH] Checking for pending device token...");
  
  // First check storage for saved token
  const savedToken = await storage.getItem("push_token");
  const tokenToRegister = pendingDeviceToken || savedToken;
  
  if (tokenToRegister) {
    console.log("[PUSH] Found pending/saved token, attempting registration...");
    return await registerDeviceWithBackend(tokenToRegister);
  }
  
  console.log("[PUSH] No pending token to register");
  return false;
};

// Setup push notification listeners (only once)
const setupListeners = () => {
  if (listenersRegistered) {
    console.log("[PUSH] Push notification listeners already registered");
    return;
  }
  
  console.log("[PUSH] Setting up push notification listeners...");
  
  // Listen for registration success
  PushNotifications.addListener("registration", async (token) => {
    console.log("[PUSH] ========================================");
    console.log("[PUSH] APNs token received from iOS!");
    console.log("[PUSH] Token:", token.value);
    console.log("[PUSH] ========================================");
    
    // Save token locally first - this is critical
    await storage.setItem("push_token", token.value);
    await storage.setItem("push_enabled", "true");
    console.log("[PUSH] Token saved to local storage");
    
    // Try to register with backend immediately
    const success = await registerDeviceWithBackend(token.value);
    console.log("[PUSH] Backend registration result:", success);
  });

  // Listen for registration errors
  PushNotifications.addListener("registrationError", (error) => {
    console.error("[PUSH] ========================================");
    console.error("[PUSH] Push notification registration ERROR!");
    console.error("[PUSH] Error:", JSON.stringify(error));
    console.error("[PUSH] ========================================");
  });

  // Listen for push notifications received while app is open
  PushNotifications.addListener("pushNotificationReceived", (notification) => {
    console.log("Push notification received:", notification);
  });

  // Listen for push notification action (when user taps on notification)
  PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    console.log("Push notification action:", action);
    const data = action.notification.data;
    if (data?.contactId) {
      window.location.href = `/contacts/${data.contactId}`;
    }
  });
  
  listenersRegistered = true;
  console.log("Push notification listeners registered");
};

// Initialize push notifications
export const initPushNotifications = async () => {
  if (!isNative) {
    console.log("Push notifications only available on native platforms");
    return null;
  }

  try {
    // Setup listeners first (only once)
    setupListeners();
    
    // Request permission
    const permResult = await PushNotifications.requestPermissions();
    console.log("Push permission result:", permResult.receive);
    
    if (permResult.receive === "granted") {
      // Register for push notifications - this will trigger the "registration" listener
      console.log("Requesting APNs registration...");
      await PushNotifications.register();
      
      await storage.setItem("push_enabled", "true");
      return true;
    } else {
      console.log("Push notification permission denied");
      await storage.setItem("push_enabled", "false");
      return false;
    }
  } catch (error) {
    console.error("Error initializing push notifications:", error);
    return false;
  }
};

// Check if push notifications are enabled
export const checkPushPermissions = async () => {
  if (!isNative) return false;
  
  try {
    const result = await PushNotifications.checkPermissions();
    return result.receive === "granted";
  } catch (error) {
    return false;
  }
};

// Check if device is already registered on backend
export const checkDeviceRegistration = async () => {
  try {
    const response = await api.get("/notifications/device-status");
    return response.data?.has_devices || false;
  } catch (error) {
    console.error("Error checking device registration:", error);
    return false;
  }
};

// Re-register device if needed (called on app startup or after login)
export const ensureDeviceRegistered = async () => {
  if (!isNative) return false;
  
  try {
    console.log("[PUSH] ensureDeviceRegistered called");
    
    // Setup listeners first
    setupListeners();
    
    // Check permissions first
    const hasPermission = await checkPushPermissions();
    if (!hasPermission) {
      console.log("[PUSH] Push permissions not granted, cannot register device");
      return false;
    }
    
    // Ensure auth token is set - CRITICAL for API calls
    const hasAuth = await ensureAuthToken();
    console.log("[PUSH] Auth token available:", hasAuth);
    
    if (!hasAuth) {
      console.log("[PUSH] No auth token available for device registration - will retry later");
      return false;
    }
    
    // Check if we have a saved token
    const savedToken = await storage.getItem("push_token");
    console.log("[PUSH] Saved push token:", savedToken ? savedToken.substring(0, 30) + "..." : "none");
    
    if (savedToken) {
      // Re-register the saved token with backend
      console.log("[PUSH] Attempting to register saved token with backend...");
      const success = await registerDeviceWithBackend(savedToken);
      console.log("[PUSH] Registration result:", success);
      if (success) {
        return true;
      }
    }
    
    // If no saved token or registration failed, request new token from APNs
    console.log("[PUSH] Requesting new APNs token from iOS...");
    await PushNotifications.register();
    
    // Wait a bit for the registration callback to complete
    console.log("[PUSH] Waiting for APNs token callback...");
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    // Check if we got a token now
    const newToken = await storage.getItem("push_token");
    console.log("[PUSH] New token after wait:", newToken ? newToken.substring(0, 30) + "..." : "none");
    
    if (newToken) {
      return await registerDeviceWithBackend(newToken);
    }
    
    console.log("[PUSH] No token received from APNs");
    return false;
    
  } catch (error) {
    console.error("[PUSH] Error ensuring device registration:", error);
    return false;
  }
};

// Clear app badge on iOS (via backend API which sends silent push)
export const clearAppBadge = async () => {
  if (!isNative) return;
  
  try {
    // Notify backend to send silent push to clear badge on all devices
    const hasAuth = await ensureAuthToken();
    if (hasAuth) {
      try {
        const response = await api.post("/notifications/clear-badge");
        console.log("[PUSH] Badge cleared:", response.data);
      } catch (e) {
        console.log("[PUSH] Backend badge clear failed:", e.message);
      }
    } else {
      console.log("[PUSH] No auth token, cannot clear badge via backend");
    }
  } catch (error) {
    console.log("[PUSH] Error clearing badge:", error);
  }
};

export default { 
  initPushNotifications, 
  checkPushPermissions,
  checkDeviceRegistration,
  ensureDeviceRegistered,
  registerPendingDeviceToken,
  registerDeviceWithBackend,
  clearAppBadge
};
